import React from "react";
import { useLang } from "../../i18n/LanguageContext";

// Sober supply-chain network: nodes (suppliers, factory, warehouse, transport,
// customers) with animated data flows and an event pulse propagating + an
// alternative decision path highlighted. No stock AI imagery — pure diagram.
const nodes = [
  { id: "s1", x: 60, y: 70, labelKey: "supplier", type: "supplier" },
  { id: "s2", x: 60, y: 210, labelKey: "supplier", type: "supplier" },
  { id: "f1", x: 230, y: 140, labelKey: "factory", type: "factory" },
  { id: "w1", x: 400, y: 80, labelKey: "warehouse", type: "warehouse" },
  { id: "w2", x: 400, y: 210, labelKey: "warehouse", type: "warehouse" },
  { id: "t1", x: 560, y: 140, labelKey: "transport", type: "transport" },
  { id: "c1", x: 700, y: 80, labelKey: "customer", type: "customer" },
  { id: "c2", x: 700, y: 210, labelKey: "customer", type: "customer" },
];

const edges = [
  ["s1", "f1"], ["s2", "f1"], ["f1", "w1"], ["f1", "w2"],
  ["w1", "t1"], ["w2", "t1"], ["t1", "c1"], ["t1", "c2"],
];

const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));

// the "event" propagates on this path; alternative path is highlighted gold
const eventPath = ["s2", "f1", "w2"];
const altPath = ["f1", "w1", "t1", "c1"];

const inPath = (a, b, path) => {
  for (let i = 0; i < path.length - 1; i++) {
    if (path[i] === a && path[i + 1] === b) return true;
  }
  return false;
};

export const SupplyChainNetwork = () => {
  const { t } = useLang();
  const network = t.home.network;
  return (
    <div className="w-full" data-testid="supply-chain-network">
      <svg viewBox="0 0 760 290" className="w-full h-auto" role="img"
           aria-label={network.ariaLabel}>
        {/* edges */}
        {edges.map(([a, b], i) => {
          const na = nodeById[a], nb = nodeById[b];
          const isEvent = inPath(a, b, eventPath);
          const isAlt = inPath(a, b, altPath);
          return (
            <g key={i}>
              <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                    stroke={isAlt ? "#D4AF37" : isEvent ? "#00F0FF" : "#1F2937"}
                    strokeWidth={isAlt || isEvent ? 1.6 : 1} />
              {(isEvent || isAlt) && (
                <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                      stroke={isAlt ? "#D4AF37" : "#00F0FF"} strokeWidth="1.6"
                      className="dash-move" opacity="0.9" />
              )}
            </g>
          );
        })}

        {/* nodes */}
        {nodes.map((n) => {
          const onEvent = eventPath.includes(n.id);
          const onAlt = altPath.includes(n.id);
          const color = onEvent ? "#00F0FF" : onAlt ? "#D4AF37" : "#9CA3AF";
          return (
            <g key={n.id}>
              {(onEvent || onAlt) && (
                <circle cx={n.x} cy={n.y} r="12" fill="none" stroke={color} strokeWidth="1"
                        className="pulse-node" opacity="0.5" />
              )}
              <rect x={n.x - 6} y={n.y - 6} width="12" height="12"
                    fill={onEvent || onAlt ? color : "#0B1120"} stroke={color} strokeWidth="1.4"
                    transform={n.type === "customer" ? `rotate(45 ${n.x} ${n.y})` : ""} />
              <text x={n.x} y={n.y + 26} textAnchor="middle" fontSize="10"
                    fill="#9CA3AF" fontFamily="IBM Plex Mono, monospace">{network[n.labelKey]}</text>
            </g>
          );
        })}
      </svg>

      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-xs font-mono text-cool-light">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 border border-cyan bg-cyan/70 inline-block" />{network.event}</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 border border-gold bg-gold/70 inline-block" />{network.alternative}</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 border border-cool-light inline-block" />{network.network}</span>
      </div>
    </div>
  );
};
